import { useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { getDeviceID } from "./storeApi"
import { initAnalytics, reportPurchaseSent } from "./analytics"

/**
 * Mounts the first-party analytics session (old components/AldooAnalytics):
 * the device id of the signed-in credentials, or the persistent anonymous
 * device, opens the session; the user id resolver is kept fresh on login.
 */
// an account made in the last minutes is a new one (a Mongo id begins with its creation time)
const NEW_ACCOUNT_MS = 10 * 60 * 1000
const isNewAccount = (id) => {
  const seconds = parseInt(String(id || "").slice(0, 8), 16)
  return Number.isFinite(seconds) && Date.now() - seconds * 1000 < NEW_ACCOUNT_MS
}

export default function AldooAnalytics() {
  const [auth] = PlatformAuth.useAuth()
  // a purchase the page sent to Google Analytics: the server need not send it again
  useEffect(() => {
    const token = PubSub.subscribe("Tracking_Purchase", (_, data) => {
      if (window.__aldooTracking?.ga4Id && String(data?.transactionId || "").startsWith("pi_")) reportPurchaseSent(data.transactionId)
    })
    return () => PubSub.unsubscribe(token)
  }, [])

  // who was signed in when the page knew it first: a change after that is a sign-in (GA4 login / sign_up)
  const knownUser = useRef(undefined)

  useEffect(() => {
    if (auth === undefined) return
    const id = auth?._id || null
    // the visit's user for Google Analytics (its own id, never the email)
    if (typeof window.gtag === "function") window.gtag("set", { user_id: id || undefined })
    if (knownUser.current === undefined) {
      knownUser.current = id
      return
    }
    if (id && id !== knownUser.current) {
      if (isNewAccount(id)) PubSub.publish("Tracking_NewAccountCreated", { userId: id, email: auth?.email, method: "email" })
      else PubSub.publish("Tracking_Login", { method: "email" })
    }
    knownUser.current = id
  }, [auth?._id])

  useEffect(() => {
    if (auth === undefined) return
    const getUserID = () => auth?._id || null
    if (auth?.deviceID) {
      initAnalytics({ deviceID: auth.deviceID, getUserID })
      return
    }
    getDeviceID()
      .then((deviceID) => initAnalytics({ deviceID, getUserID }))
      .catch(() => {})
  }, [auth])

  return null
}
